import { SelectOption } from '@repo/ui/components/multiselect/index';
import { Badge } from '@repo/ui/components/ui/badge';
import { Input } from '@repo/ui/components/ui/input';
import { Label } from '@repo/ui/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@repo/ui/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/components/ui/select';
import { Textarea } from '@repo/ui/components/ui/textarea';
import React from 'react';
import ReactSelect from 'react-select';
import { arbreFields, epaysageFields, fieldsWoodarea } from '@repo/field-configs';
import { Field } from '../types';
import { ImageUpload } from './image-upload';

type FormType = 'arbre' | 'epaysage' | 'essence';

interface FormFieldProps {
  field: Field;
  value: any;
  error?: string;
  onChange: (fieldName: string, value: any) => void;
  renderFreeforms: (fieldName: string) => React.ReactNode;
  dynamicOptions?: { id: string; label: string }[];
  formType?: FormType;
}

export function FormField({ field, value, error, onChange, renderFreeforms, dynamicOptions, formType = 'arbre' }: FormFieldProps) {
  // Use dynamic options if provided, otherwise fall back to field options
  const options = dynamicOptions || field.options || [];

  // Get the appropriate JSON fields based on form type (single source: @repo/field-configs)
  const getJsonFields = (): any => {
    switch (formType) {
      case 'epaysage':
        return epaysageFields;
      case 'essence':
        return fieldsWoodarea;
      case 'arbre':
      default:
        return arbreFields;
    }
  };

  const jsonFields = getJsonFields();

  switch (field.type) {
    case 'text':
    case 'number':
      return (
        <div className="space-y-2">
          <Label htmlFor={field.name} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <div className="flex items-center space-x-2">
            <Input
              id={field.name}
              type={field.type}
              value={value}
              onChange={(e) => onChange(field.name, field.type === 'number' ? Number(e.target.value) : e.target.value)}
              placeholder={field.placeholder || `Entrez ${field.label.toLowerCase()}`}
              className={error ? 'border-red-500' : ''}
            />
            {field.unit && (
              <Badge variant="secondary" className="whitespace-nowrap">
                {field.unit}
              </Badge>
            )}
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          {renderFreeforms(field.name)}
        </div>
      );

    case 'textarea':
      return (
        <div className="space-y-2">
          <Label htmlFor={field.name} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Textarea
            id={field.name}
            value={value}
            onChange={(e) => onChange(field.name, e.target.value)}
            placeholder={field.placeholder || `Entrez ${field.label.toLowerCase()}`}
            className={error ? 'border-red-500' : ''}
            rows={4}
          />
          {error && <p className="text-sm text-red-500">{error}</p>}
          {renderFreeforms(field.name)}
        </div>
      );

    case 'multiselect': {
      const currentValues: string[] = Array.isArray(value) ? value : [];
      const jsonField: any = (jsonFields as any)[field.name];
      const rg: Record<string, string> | undefined = jsonField?.rg;
      const isExclusion = (optionId: string) => rg && rg[optionId] === 'exclusion';
      const exclusionSelected = currentValues.some((v) => isExclusion(v));

      // Create options for ReactSelect including freeforms
      const selectOptions: SelectOption<string>[] = [
        ...options.map(option => ({
          value: option.id,
          label: option.label,
          isDisabled: exclusionSelected && !currentValues.includes(option.id) && !isExclusion(option.id)
        })),
        ...(jsonField?.freeforms || []).map((ff: any) => ({
          value: ff.id,
          label: ff.label || 'Autre, précisez :',
          isDisabled: exclusionSelected && !currentValues.includes(ff.id) && !isExclusion(ff.id)
        }))
      ];

      // Convert current values to react-select format
      const selectedOptions = selectOptions.filter(opt => currentValues.includes(opt.value));

      return (
        <div className="space-y-3">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>

          <ReactSelect<SelectOption<string>, true>
            options={selectOptions}
            value={selectedOptions}
            onChange={(newValues) => {
              const selectedIds = (newValues as SelectOption<string>[] || []).map(opt => opt.value);
              const next = new Set(selectedIds);

              // Handle exclusion logic
              const selectedExclusion = Array.from(next).find(v => isExclusion(v));
              if (selectedExclusion) {
                // If exclusion is selected, clear all other options
                next.clear();
                next.add(selectedExclusion);
              } else {
                // If non-exclusion is selected, remove any exclusions
                Object.entries(rg || {}).forEach(([opt, rule]) => {
                  if (rule === 'exclusion') next.delete(opt);
                });
              }

              // Handle oneOf rule: if selecting a oneOf option, deselect all other oneOf options
              const oneOfValues = jsonField?.oneOf || [];
              if (oneOfValues.length > 0) {
                const selectedOneOfOptions = Array.from(next).filter(v => oneOfValues.includes(v));
                if (selectedOneOfOptions.length > 1) {
                  // Keep only the last selected oneOf option
                  const lastSelectedOneOf = selectedOneOfOptions[selectedOneOfOptions.length - 1];
                  oneOfValues.forEach((oneOfValue: string) => {
                    if (oneOfValue !== lastSelectedOneOf) {
                      next.delete(oneOfValue);
                    }
                  });
                } else if (selectedOneOfOptions.length === 1) {
                  // Ensure no other oneOf options are selected
                  oneOfValues.forEach((oneOfValue: string) => {
                    if (oneOfValue !== selectedOneOfOptions[0]) {
                      next.delete(oneOfValue);
                    }
                  });
                }
              }

              // Check maxSelected limit
              const finalValues = Array.from(next);
              if (jsonField?.max && finalValues.length > jsonField.max) {
                return; // Don't update if max is exceeded
              }

              onChange(field.name, finalValues);
            }}
            placeholder={`Sélectionnez ${field.label.toLowerCase()}`}
            isMulti={true}
            isSearchable={true}
            isClearable={true}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              control: (base: any) => ({
                ...base,
                minHeight: '40px',
                border: '2px solid hsl(var(--input))',
                '&:hover': {
                  border: '2px solid hsl(var(--primary) / 0.2)',
                },
              }),
              placeholder: (base: any) => ({
                ...base,
                color: 'hsl(var(--muted-foreground))',
              }),
            }}
          />

          {error && <p className="text-sm text-red-500">{error}</p>}
          {renderFreeforms(field.name)}
        </div>
      );
    }

    case 'radio':
      // Create options for ReactSelect including freeforms
      const jsonField: any = (jsonFields as any)[field.name];
      const radioOptions: SelectOption<string>[] = [
        ...options.map(option => ({
          value: option.id,
          label: option.label
        })),
        ...(jsonField?.freeforms || []).map((ff: any) => ({
          value: ff.id,
          label: ff.label || 'Autre, précisez :'
        }))
      ];

      // Find selected option
      const selectedOption = radioOptions.find(opt => opt.value === value) || null;

      return (
        <div className="space-y-3">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>

          <ReactSelect<SelectOption<string>, false>
            options={radioOptions}
            value={selectedOption}
            onChange={(newValue) => onChange(field.name, newValue?.value || '')}
            placeholder={`Sélectionnez ${field.label.toLowerCase()}`}
            isSearchable={true}
            isClearable={true}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              control: (base: any) => ({
                ...base,
                minHeight: '40px',
                border: '2px solid hsl(var(--input))',
                '&:hover': {
                  border: '2px solid hsl(var(--primary) / 0.2)',
                },
              }),
              placeholder: (base: any) => ({
                ...base,
                color: 'hsl(var(--muted-foreground))',
              }),
            }}
          />

          {error && <p className="text-sm text-red-500">{error}</p>}
          {renderFreeforms(field.name)}
        </div>
      );

    case 'select':
      return (
        <div className="space-y-2">
          <Label htmlFor={field.name} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Select value={value} onValueChange={(val) => onChange(field.name, val)}>
            <SelectTrigger className={error ? 'border-red-500' : ''}>
              <SelectValue placeholder={`Sélectionnez ${field.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {error && <p className="text-sm text-red-500">{error}</p>}
          {renderFreeforms(field.name)}
        </div>
      );

    case 'table':
      return (
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <div className="border rounded-lg p-4">
            <div className="grid gap-2" style={{ gridTemplateColumns: `1fr repeat(${field.tableConfig?.cols.length || 1}, 1fr)` }}>
              <div className="font-medium text-sm"></div>
              {field.tableConfig?.cols.map((col) => (
                <div key={col.id} className="font-medium text-sm text-center">
                  {col.label}
                </div>
              ))}
              {field.tableConfig?.rows.map((row) => (
                <React.Fragment key={row}>
                  <div className="text-sm">{row}</div>
                  {field.tableConfig?.cols.map((col) => (
                    <div key={col.id} className="flex justify-center">
                      <RadioGroup
                        value={value?.[row] || ''}
                        onValueChange={(val) => {
                          const newValue = { ...value, [row]: val };
                          onChange(field.name, newValue);
                        }}
                      >
                        <RadioGroupItem value={col.id} />
                      </RadioGroup>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          {renderFreeforms(field.name)}
        </div>
      );

    case 'image-upload':
      return (
        <ImageUpload
          name={field.name}
          label={field.label}
          value={value}
          onChange={onChange}
          required={field.required}
          imageConfig={field.imageConfig}
          error={error}
        />
      );

    default:
      return null;
  }
}
